import React, { useRef, useEffect } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { mergeClassNames, useIsUnmounted } from '../../../../utils';
import { headerHeight } from '../../../../utils/menuHeight';
import styles from './index.module.scss';

export default function AnimatedImageSequence({
  imageUrls,
  imageCache,
  scrollPositionSpring, // takes a spring and uses scrollTeleport - a value from 0 to 1 defining the image sequence opacity and `sequenceCanvasFixed`: true/false
  size = { width: 1920, height: 1080 },
  className = '',
  style = null,
}) {
  let isUnmounted = useIsUnmounted();
  let canvasRef = useRef(null);
  let imgRef = useRef(null);
  let ctxRef = useRef(null);

  style = {
    opacity: scrollPositionSpring.scrollTeleportOpacity,
    // better to decide this centrally?
    position: scrollPositionSpring.sequenceCanvasFixed?.to(isFixed => (isFixed ? 'fixed' : 'absolute')),
    bottom: scrollPositionSpring.sequenceCanvasFixed?.to(isFixed => (isFixed ? 'unset' : '0px')),
    top: scrollPositionSpring.sequenceCanvasFixed?.to(isFixed => (isFixed ? `${headerHeight}px` : 'unset')),
    ...style,
  };

  useSpring(
    () => ({
      animationIndex: scrollPositionSpring.animationIndex,
      onChange: ({ value: { animationIndex } }) => {
        // need this constraint 👇 since the keyframe for the last frame typically is set to the count of frames + 1 when we start to count from 1 while the other keyframes is spot on
        animationIndex = Math.min(Math.round(animationIndex), imageUrls.length - 1);
        let imageUrl = imageCache[animationIndex] || imageUrls[animationIndex];
        if (ctxRef.current && imgRef.current) {
          imgRef.current.src = imageUrl;
        }
      },
    }),
    [imageCache, imageUrls, scrollPositionSpring]
  );

  useEffect(() => {
    let imageUrl = imageCache[0] || imageUrls[0]; // this should be done only once?
    let img = new Image();
    imgRef.current = img;
    img.onload = function() {
      if (!isUnmounted.current && size) {
        let canvas = canvasRef.current;
        if (canvas) {
          let ctx = canvas.getContext('2d');
          ctxRef.current = ctx;
          ctx.drawImage(img, 0, 0, size.width, size.height);
        }
      }
    };
    img.src = imageUrl;
  }, [imageCache, imageUrls, isUnmounted, size]);

  return (
    <animated.div className={mergeClassNames(styles.imageSequence, className)} style={style}>
      {size && <canvas ref={canvasRef} id="sequence" width={size.width} height={size.height} />}
    </animated.div>
  );
}

export * from './usePreloadingImageSequence';
